"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Menu,
  Message,
  Modal,
} from "semantic-ui-react";

import { slashAppAnnouncements } from "../../../utils/slashAppAnnouncements";

const TARGET_ALL = "all_instructors";
const TARGET_CUSTOM = "custom";

/**
 * インストラクター向けお知らせの作成/編集フォーム。
 *
 * 会員向け一斉送信 (吹き出し/チャット配信) とは独立した自己完結コンポーネント。
 * ブロードキャスト編集画面の「対象: インストラクター」で表示される。
 * 送信すると slash-app 側で対象インストラクターの「お知らせ」タブに配信される
 * (alerts へファンアウト + プッシュ通知)。
 *
 * @param {{ editingId: string|null, onDone: () => void, onCancel: () => void }} props
 */
function InstructorAnnouncementForm({ editingId, onDone, onCancel }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [targetType, setTargetType] = useState(TARGET_ALL);
  const [selectedInstructors, setSelectedInstructors] = useState([]); // number[]

  const [instructors, setInstructors] = useState([]);
  const [loadingDraft, setLoadingDraft] = useState(!!editingId);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState(null);

  // インストラクター一覧 (指定送信ピッカー用)。
  useEffect(() => {
    let alive = true;
    slashAppAnnouncements
      .listInstructors()
      .then((data) => {
        if (alive) setInstructors(data.results || []);
      })
      .catch(() => {
        if (alive) setInstructors([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  // 既存下書きの読み込み。
  useEffect(() => {
    if (!editingId) return;
    let alive = true;
    slashAppAnnouncements
      .detail(editingId)
      .then((data) => {
        if (!alive) return;
        setTitle(data.title || "");
        setBody(data.body || "");
        setTargetType(data.target_type || TARGET_ALL);
        setSelectedInstructors(
          Array.isArray(data.target_instructors) ? data.target_instructors : []
        );
        setLoadingDraft(false);
      })
      .catch(() => {
        if (!alive) return;
        setError("お知らせの読み込みに失敗しました");
        setLoadingDraft(false);
      });
    return () => {
      alive = false;
    };
  }, [editingId]);

  const instructorOptions = useMemo(
    () =>
      instructors.map((i) => ({
        key: i.id,
        value: i.id,
        text: i.username,
      })),
    [instructors]
  );

  const validation = useMemo(() => {
    if (!title.trim()) return { ok: false, msg: "タイトルを入力してください。" };
    if (!body.trim()) return { ok: false, msg: "本文を入力してください。" };
    if (targetType === TARGET_CUSTOM && selectedInstructors.length === 0) {
      return {
        ok: false,
        msg: "対象のインストラクターを 1 名以上選択してください。",
      };
    }
    return { ok: true };
  }, [title, body, targetType, selectedInstructors]);

  const buildPayload = (isDraft) => ({
    title: title.trim(),
    body: body.trim(),
    target_type: targetType,
    target_instructors: targetType === TARGET_CUSTOM ? selectedInstructors : [],
    is_draft: isDraft,
  });

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (editingId) {
        await slashAppAnnouncements.update(editingId, buildPayload(false));
        await slashAppAnnouncements.send(editingId);
      } else {
        await slashAppAnnouncements.create(buildPayload(false));
      }
      setConfirmOpen(false);
      onDone();
    } catch (e) {
      setError(e.message || "送信に失敗しました");
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    setError(null);
    try {
      if (editingId) {
        await slashAppAnnouncements.update(editingId, buildPayload(true));
      } else {
        await slashAppAnnouncements.create(buildPayload(true));
      }
      onDone();
    } catch (e) {
      setError(e.message || "下書きの保存に失敗しました");
    } finally {
      setSavingDraft(false);
    }
  };

  const targetSummary =
    targetType === TARGET_ALL
      ? "全インストラクター"
      : `選択した ${selectedInstructors.length} 名`;

  if (loadingDraft) {
    return <Message info content="読み込み中..." />;
  }

  return (
    <div className="instructor-announcement-form">
      {error ? <Message negative content={error} /> : null}

      <Form>
        <Form.Field>
          <label>対象インストラクター</label>
          <Menu pointing secondary>
            <Menu.Item
              content="全インストラクター"
              active={targetType === TARGET_ALL}
              onClick={() => setTargetType(TARGET_ALL)}
            />
            <Menu.Item
              content="インストラクターを指定"
              active={targetType === TARGET_CUSTOM}
              onClick={() => setTargetType(TARGET_CUSTOM)}
            />
          </Menu>
        </Form.Field>

        {targetType === TARGET_CUSTOM ? (
          <Form.Field>
            <label>送信先インストラクター</label>
            <Dropdown
              placeholder="インストラクターを選択"
              fluid
              multiple
              search
              selection
              options={instructorOptions}
              value={selectedInstructors}
              onChange={(_, { value }) => setSelectedInstructors(value)}
            />
          </Form.Field>
        ) : null}

        <Divider />

        <Form.Input
          label="タイトル"
          placeholder="お知らせのタイトル"
          value={title}
          maxLength={255}
          onChange={(_, { value }) => setTitle(value)}
        />

        <Form.TextArea
          label="本文"
          placeholder="お知らせの本文"
          value={body}
          rows={8}
          onChange={(_, { value }) => setBody(value)}
        />
      </Form>

      <Divider />

      <div className="form-actions">
        <Button
          type="button"
          className="bc-cancel"
          disabled={submitting || savingDraft}
          onClick={onCancel}
        >
          キャンセル
        </Button>
        <Button
          type="button"
          className="bc-draft"
          loading={savingDraft}
          disabled={!validation.ok || submitting || savingDraft}
          onClick={saveDraft}
        >
          下書き保存
        </Button>
        <Button
          type="button"
          primary
          loading={submitting}
          disabled={!validation.ok || submitting || savingDraft}
          onClick={() => setConfirmOpen(true)}
        >
          送信
        </Button>
      </div>

      <Modal open={confirmOpen} size="tiny" onClose={() => setConfirmOpen(false)}>
        <Modal.Header>お知らせを送信</Modal.Header>
        <Modal.Content>
          <p>
            <b>対象:</b> {targetSummary}
          </p>
          <p>
            <b>タイトル:</b> {title}
          </p>
          <p style={{ marginBottom: 4 }}>
            <b>本文:</b>
          </p>
          <div
            style={{
              whiteSpace: "pre-wrap",
              maxHeight: 220,
              overflowY: "auto",
              padding: "8px 10px",
              borderRadius: 6,
              background: "rgba(0,0,0,0.04)",
            }}
          >
            {body}
          </div>
          <p style={{ marginTop: 12 }}>
            この内容でインストラクターに送信します。よろしいですか？
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
            キャンセル
          </Button>
          <Button primary loading={submitting} onClick={submit}>
            送信する
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}

export default InstructorAnnouncementForm;
